/**
 * Ties `InputController` to whichever real devices are present: keyboard
 * always, touch when a control surface is mounted, gamepad when one is
 * connected. `collectActions()` is called once per engine tick from the game
 * loop and returns exactly the `InputAction`s the engine should see this
 * tick — DOM events fire asynchronously between ticks, so presses are
 * buffered and drained here rather than dispatched straight to the engine.
 */
import { TICK_MS } from '@interlock/engine';
import type { InputAction } from '@interlock/engine';

import type { ControllerAction, InputControllerConfig } from './input-controller.js';
import { InputController } from './input-controller.js';
import { bindKeyboard } from './keyboard.js';
import type { KeyboardHandle } from './keyboard.js';
import { bindTouchControls } from './touch-controls.js';
import type { TouchControlsHandle } from './touch-controls.js';
import { pollGamepad } from './gamepad.js';
import type { GamepadSnapshot } from './gamepad.js';

export interface InputSessionOptions {
  readonly touchRoot?: HTMLElement | null;
  readonly config?: InputControllerConfig;
  readonly enableGamepad?: boolean;
}

export class InputSession {
  readonly controller: InputController;
  private readonly immediate: ControllerAction[] = [];
  private readonly keyboardHandle: KeyboardHandle;
  private touchHandle: TouchControlsHandle | null = null;
  private gamepadSnapshot: GamepadSnapshot | null = null;
  private readonly gamepadEnabled: boolean;
  private suspended = false;

  constructor(options: InputSessionOptions = {}) {
    this.controller = new InputController(options.config);
    this.gamepadEnabled =
      options.enableGamepad ?? (typeof navigator !== 'undefined' && 'getGamepads' in navigator);

    this.keyboardHandle = bindKeyboard({
      controller: this.controller,
      onFire: (actions) => {
        this.fire(actions);
      },
    });

    if (options.touchRoot) this.attachTouch(options.touchRoot);
  }

  attachTouch(root: HTMLElement): void {
    this.touchHandle?.destroy();
    this.touchHandle = bindTouchControls({
      root,
      controller: this.controller,
      onFire: (actions) => {
        this.fire(actions);
      },
    });
  }

  /** New DAS/ARR timings, applied to the run in progress (the Settings steppers). */
  setConfig(config: InputControllerConfig): void {
    this.controller.configure(config);
  }

  /**
   * Stop, or start again, turning device events into actions.
   *
   * A paused run must not collect input: a key pressed while a sheet is open
   * over the game would otherwise sit in the buffer and fire on the first
   * tick after it closes. Suspending releases everything held and empties
   * the buffer; while suspended, presses are dropped as they arrive. The
   * device listeners themselves stay attached — a release still has to reach
   * the controller.
   */
  setSuspended(suspended: boolean): void {
    if (this.suspended === suspended) return;
    this.suspended = suspended;
    this.controller.releaseAll();
    this.immediate.length = 0;
    this.gamepadSnapshot = null;
  }

  get isSuspended(): boolean {
    return this.suspended;
  }

  private fire(actions: readonly ControllerAction[]): void {
    if (this.suspended) {
      // The press reached the controller before this callback ran; undo it so
      // nothing is left "held" for DAS to repeat once play resumes.
      this.controller.releaseAll();
      return;
    }
    this.immediate.push(...actions);
  }

  /** Called once per engine tick. Order: buffered immediate presses, then this tick's DAS/ARR repeats. */
  collectActions(): readonly InputAction[] {
    if (this.suspended) return [];
    if (this.gamepadEnabled) {
      const { snapshot, fired } = pollGamepad(this.controller, this.gamepadSnapshot);
      this.gamepadSnapshot = snapshot;
      this.immediate.push(...fired);
    }

    const repeats = this.controller.advance(TICK_MS);
    const actions: InputAction[] = [...this.immediate, ...repeats];
    this.immediate.length = 0;
    return actions;
  }

  destroy(): void {
    this.keyboardHandle.destroy();
    this.touchHandle?.destroy();
  }
}
